import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Download, X } from "lucide-react";

/**
 * PWA install banner — appears when browser fires `beforeinstallprompt`.
 * Dismissed state persists in localStorage so it doesn't reappear after dismiss.
 */
const PWAInstallBanner = () => {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("pwa-install-dismissed") === "1") return;

    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setPrompt(null);
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem("pwa-install-dismissed", "1");
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-80 z-50 bg-brand-panel border border-brand-border rounded-xl shadow-lg p-4 flex items-start gap-3">
      <div className="w-10 h-10 bg-brand-ink rounded-lg flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5 text-brand-on-ink" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-brand-ink">Install TymLogr</p>
        <p className="text-xs text-brand-steel mt-0.5">Add to your home screen for quick access.</p>
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            className="bg-brand-ink text-brand-on-ink hover:bg-brand-ink/90 h-7 text-xs disabled:bg-brand-border disabled:text-brand-steel disabled:opacity-100"
            onClick={handleInstall}
          >
            Install
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-brand-steel" onClick={handleDismiss}>
            Not now
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="text-brand-steel hover:text-brand-ink flex-shrink-0"
        aria-label="Dismiss install prompt"
      >
        <X className="w-4 h-4" aria-hidden />
      </button>
    </div>
  );
};

export default PWAInstallBanner;
